import { useCallback, useEffect, useState } from "react";
import {
  NavContext,
  type NavContextValue,
  type PageId,
  type SettingsTab,
} from "./context/NavContext";
import Landing from "./pages/Landing";
import Dashboard from "./pages/Dashboard";
import Nouvelle from "./pages/Nouvelle";
import Fiches from "./pages/Fiches";
import Fiche from "./pages/Fiche";
import Pricing from "./pages/Pricing";
import Brand from "./pages/Brand";
import Settings from "./pages/Settings";
import Legal from "./pages/Legal";
import { CookieBanner } from "./components/CookieBanner";
import { CheckoutToast, type CheckoutToastKind } from "./components/CheckoutToast";
import { packs as pricingPacks } from "./data/pricing";
import { enableAnalyticsWithConsent } from "./lib/firebase";
import { hasConsent, onConsentChange } from "./lib/consent";

export default function App() {
  const [page, setPage] = useState<PageId>("landing");
  const [ficheId, setFicheId] = useState<string | null>(null);
  const [settingsTab, setSettingsTab] = useState<SettingsTab | null>(null);
  const [checkoutToast, setCheckoutToast] = useState<{
    kind: CheckoutToastKind;
    quantity?: number;
  } | null>(null);

  const go = useCallback<NavContextValue["go"]>((p, opts) => {
    setPage(p);
    if (opts?.ficheId !== undefined) setFicheId(opts.ficheId);
    if (p !== "fiche") setFicheId(null);
    if (opts?.settingsTab !== undefined) setSettingsTab(opts.settingsTab);
    if (p !== "settings") setSettingsTab(null);
  }, []);

  // Retour depuis Stripe Checkout (success / cancel). La query vient du
  // `success_url` / `cancel_url` fixés par `createCheckoutSession`. Le
  // billing doc a déjà été maj côté serveur par le webhook à ce stade
  // (success_url est atteint après la confirmation du paiement). On lit
  // les params au mount puis on les retire de l'URL pour éviter un re-
  // trigger au rechargement.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get("checkout");
    const kind = params.get("kind"); // "subscription" | "topup" | "pack"
    const portal = params.get("portal");
    if (checkout === "success") {
      // Atterrit directement sur l'onglet Abonnement — c'est là que le
      // user voit son sub fraîchement créé / ses crédits ajoutés (incl.
      // topUps[]). Plus satisfaisant qu'un atterrissage sur Profil.
      setPage("settings");
      setSettingsTab("abonnement");

      // Choisit la copy du toast selon le `kind` injecté par
      // createCheckoutSession dans la success_url. Le quantity vient des
      // mêmes params (qty pour topup, packId mappé pour pack).
      if (kind === "topup") {
        const qty = Number(params.get("qty") ?? 0);
        setCheckoutToast({
          kind: "topup-success",
          quantity: Number.isFinite(qty) && qty > 0 ? qty : undefined,
        });
      } else if (kind === "pack") {
        const packId = params.get("packId");
        const pack = pricingPacks.find((p) => p.id === packId);
        setCheckoutToast({
          kind: "pack-success",
          quantity: pack?.quantity,
        });
      } else {
        // subscription (default fallback aussi pour les vieilles URLs sans `kind`)
        setCheckoutToast({ kind: "subscription-success" });
      }
      window.history.replaceState(null, "", window.location.pathname);
    } else if (checkout === "cancel") {
      setPage("pricing");
      setCheckoutToast({ kind: "cancel" });
      window.history.replaceState(null, "", window.location.pathname);
    } else if (portal === "return") {
      setPage("settings");
      setSettingsTab("abonnement");
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
  }, [page]);

  // Bootstrap Analytics conditionné au consentement. Au mount, si l'utilisateur
  // avait déjà accepté (dans une visite précédente et pas périmé), on charge
  // Analytics. Sinon, on s'abonne et on charge dès qu'il accepte. Note :
  // Firebase Analytics ne se "décharge" pas proprement — si l'utilisateur
  // retire son consentement en cours de session, le prochain rechargement de
  // page n'initialisera plus Analytics.
  useEffect(() => {
    if (hasConsent("analytics")) void enableAnalyticsWithConsent();
    return onConsentChange((state) => {
      if (state?.analytics) void enableAnalyticsWithConsent();
    });
  }, []);

  const pages: Record<PageId, JSX.Element> = {
    landing: <Landing />,
    dashboard: <Dashboard />,
    nouvelle: <Nouvelle />,
    fiches: <Fiches />,
    fiche: <Fiche />,
    pricing: <Pricing />,
    brand: <Brand />,
    settings: <Settings />,
    legal: <Legal />,
  };

  return (
    <NavContext.Provider value={{ go, page, ficheId, settingsTab }}>
      {pages[page]}
      {checkoutToast && (
        <CheckoutToast
          kind={checkoutToast.kind}
          quantity={checkoutToast.quantity}
          onClose={() => setCheckoutToast(null)}
        />
      )}
      <CookieBanner />
    </NavContext.Provider>
  );
}
